<script setup lang="ts">
/**
 * /resume-examples — the hub for the per-role examples. Answers "resume
 * examples" in a paragraph, then lists every page by field, with the life
 * situations (student, career change) apart, because those pages are about
 * what replaces experience rather than how to present it.
 */
import { RESUME_EXAMPLE_GROUPS, RESUME_EXAMPLES, roleName } from '~/constants/resumeExamples'

const { t, locale } = useI18n()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/priklady-zivotopisu', de: '/lebenslauf-beispiele', pl: '/przyklady-cv' } })

const groups = computed(() =>
  RESUME_EXAMPLE_GROUPS
    .map(id => ({
      id,
      items: RESUME_EXAMPLES
        .filter(item => item.group === id)
        .sort((a, b) => b.volume - a.volume || roleName(a, locale.value).localeCompare(roleName(b, locale.value), locale.value)),
    }))
    .filter(group => group.items.length > 0),
)
</script>

<template>
  <MarketingFeaturePage
    seo-key="resumeExamples"
    path="/resume-examples"
    :breadcrumb-title="t('resumeExamples.hub.heading')"
  >
    <div class="sofax">
      <section class="bg-white dark:bg-gray-950">
        <div class="section-space-sm">
          <div class="shell">
            <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
              <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
                {{ t('resumeExamples.hub.shortLabel') }}
              </div>
              <p class="text-lg">{{ t('resumeExamples.hub.short') }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="surface-alt">
        <div class="section-space">
          <div class="shell">
            <SofaxSectionHeading
              :eyebrow="t('resumeExamples.hub.list.eyebrow')"
              :title="t('resumeExamples.hub.list.title')"
              :text="t('resumeExamples.hub.list.lead')"
              width="max-w-4xl"
            />
            <div class="mx-auto flex max-w-5xl flex-col gap-10">
              <div v-for="group in groups" :key="group.id">
                <h3 class="!text-2xl">{{ t(`resumeExamples.hub.groups.${group.id}`) }}</h3>
                <ul class="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <li v-for="item in group.items" :key="item.slug">
                    <LocaleLink
                      :to="`/resume-examples/${item.slug}`"
                      class="block rounded-[0.625rem] bg-white px-5 py-4 font-semibold text-ink hover:bg-accent-soft/20 dark:bg-gray-800 dark:text-white"
                    >
                      {{ t('resumeExamples.hub.itemLabel', { role: roleName(item, locale) }) }}
                    </LocaleLink>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-white dark:bg-gray-950">
        <div class="section-space">
          <div class="shell">
            <div class="mx-auto max-w-3xl text-center">
              <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('resumeExamples.hub.next.title') }}</h2>
              <p class="mt-5 text-lg">{{ t('resumeExamples.hub.next.p1') }}</p>
              <div class="mt-8">
                <SofaxBtn to="/free-cv-builder" :label="t('resumeExamples.page.cta')" variant="accent" />
              </div>
              <p class="mt-6">
                <LocaleLink to="/how-to-write-a-resume" class="link">{{ t('resumeExamples.page.guideLink') }} →</LocaleLink>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </MarketingFeaturePage>
</template>
